import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  MessageSquare,
  Sparkles,
  ShieldAlert,
  Filter,
  Copy,
  Check,
  Reply,
  ExternalLink,
  Clock,
  Calendar,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { timeAgo, fullDate } from "@/lib/timeAgo";
import { PlatformChip, IntentChip, SeverityChip, LeadChip, ComplaintChip } from "@/components/shared/Chips";

/**
 * Customer 360 – a premium right-side "who is this person" sheet.
 *
 * Aggregates every comment, lead, and complaint from a single social-media
 * `author_handle` across ALL connected platforms into a single CRM-style
 * view. Available on every tier (no gating).
 */

interface Customer360Data {
  handle: string;
  display_handle: string;
  display_name?: string | null;
  first_seen?: string | null;
  last_seen?: string | null;
  platforms: string[];
  counts: {
    comments: number;
    leads: number;
    complaints: number;
    replies_sent: number;
  };
  intent_breakdown: Record<string, number>;
  intent_score_avg: number;
  intent_score_max: number;
  severity_breakdown: Record<string, number>;
  lead_status_breakdown: Record<string, number>;
  complaint_status_breakdown: Record<string, number>;
  response_stats: {
    total_sent: number;
    avg_response_time_seconds?: number | null;
    fastest_response_seconds?: number | null;
    last_reply_at?: string | null;
  };
  top_keywords: { keyword: string; count: number }[];
  timeline: TimelineItem[];
}

interface TimelineItem {
  type: "comment" | "lead" | "complaint";
  id: string;
  platform: string;
  date: string;
  text_preview: string;
  intent?: string | null;
  severity?: string | null;
  status?: string | null;
  intent_score?: number | null;
  replied: boolean;
  reply_sent_at?: string | null;
  reply_text?: string | null;
  post_url?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  handle: string; // canonical or `@`-prefixed handle
  /** When set, clicking "Filter by this author on Comments" narrows the current
   * list page to this handle instead of routing to /comments. */
  onFilterHere?: () => void;
}

const INTENT_LABEL: Record<string, string> = {
  buying_intent: "Buying intent",
  product_question: "Question",
  complaint: "Complaint",
  neutral: "Neutral",
  spam: "Spam",
};

const INTENT_COLOR: Record<string, string> = {
  buying_intent: "bg-violet-500",
  product_question: "bg-blue-500",
  complaint: "bg-rose-500",
  neutral: "bg-slate-400",
  spam: "bg-slate-700",
};

function formatDuration(seconds?: number | null): string {
  if (seconds == null || !isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
}

const KPI: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  icon?: React.ReactNode;
  tone?: "violet" | "emerald" | "rose" | "amber" | "slate";
  testId?: string;
}> = ({ label, value, hint, icon, tone = "slate", testId }) => {
  const tones: Record<string, string> = {
    violet: "bg-gradient-to-br from-violet-50 to-white border-violet-200 text-violet-900",
    emerald: "bg-gradient-to-br from-emerald-50 to-white border-emerald-200 text-emerald-900",
    rose: "bg-gradient-to-br from-rose-50 to-white border-rose-200 text-rose-900",
    amber: "bg-gradient-to-br from-amber-50 to-white border-amber-200 text-amber-900",
    slate: "bg-gradient-to-br from-slate-50 to-white border-slate-200 text-slate-900",
  };
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border p-3 ${tones[tone]}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold opacity-80">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-2xl font-display font-bold leading-none tracking-tight">
        {value}
      </div>
      {hint != null && (
        <div className="mt-1 text-[11px] opacity-70">{hint}</div>
      )}
    </div>
  );
};

const IntentBar: React.FC<{ breakdown: Record<string, number> }> = ({ breakdown }) => {
  const total = Object.values(breakdown).reduce((s, n) => s + n, 0);
  if (total === 0) return null;
  const order = ["buying_intent", "product_question", "complaint", "neutral", "spam"];
  const filtered = order.filter((k) => (breakdown[k] || 0) > 0);
  return (
    <div className="space-y-1.5" data-testid="c360-intent-bar">
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100">
        {filtered.map((k) => {
          const pct = ((breakdown[k] || 0) / total) * 100;
          return (
            <div
              key={k}
              className={`${INTENT_COLOR[k] || "bg-slate-300"} transition-all`}
              style={{ width: `${pct}%` }}
              title={`${INTENT_LABEL[k]}: ${breakdown[k]} (${pct.toFixed(0)}%)`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-[color:var(--sg-muted)]">
        {filtered.map((k) => (
          <span key={k} className="inline-flex items-center gap-1">
            <span className={`h-1.5 w-1.5 rounded-full ${INTENT_COLOR[k]}`} />
            {INTENT_LABEL[k]} · <b className="text-[color:var(--sg-fg)]">{breakdown[k]}</b>
          </span>
        ))}
      </div>
    </div>
  );
};

const TimelineDot: React.FC<{ type: TimelineItem["type"] }> = ({ type }) => {
  const map: Record<string, { bg: string; icon: React.ReactNode }> = {
    comment: { bg: "bg-slate-100 text-slate-700", icon: <MessageSquare className="h-3 w-3" /> },
    lead: { bg: "bg-violet-100 text-violet-700", icon: <Sparkles className="h-3 w-3" /> },
    complaint: { bg: "bg-rose-100 text-rose-700", icon: <ShieldAlert className="h-3 w-3" /> },
  };
  const cfg = map[type];
  return (
    <div
      className={`h-6 w-6 shrink-0 rounded-full grid place-items-center ${cfg.bg} ring-4 ring-white`}
      aria-hidden
    >
      {cfg.icon}
    </div>
  );
};

const TimelineEntry: React.FC<{ item: TimelineItem }> = ({ item }) => {
  const typeLabel = item.type === "comment" ? "Comment" : item.type === "lead" ? "Lead" : "Complaint";
  return (
    <div className="relative flex gap-3 py-3" data-testid={`c360-timeline-${item.id}`}>
      <TimelineDot type={item.type} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
            {typeLabel}
          </span>
          <PlatformChip platform={item.platform} />
          {item.intent && <IntentChip intent={item.intent as any} />}
          {item.severity && <SeverityChip severity={item.severity as any} />}
          {item.type === "lead" && item.status && (
            <LeadChip status={item.status as any} />
          )}
          {item.type === "complaint" && item.status && (
            <ComplaintChip status={item.status as any} />
          )}
          {item.replied && (
            <span className="sg-chip bg-emerald-50 text-emerald-700 border-emerald-200">
              <Reply className="h-3 w-3" /> Replied
            </span>
          )}
        </div>
        <p className="text-sm text-[color:var(--sg-fg)] leading-snug">
          {item.text_preview || <span className="italic text-[color:var(--sg-muted)]">(empty)</span>}
        </p>
        <div className="mt-1 text-[10px] text-[color:var(--sg-muted)] flex flex-wrap gap-x-2">
          {item.date && (
            <span title={fullDate(item.date)}>{timeAgo(item.date)}</span>
          )}
          {typeof item.intent_score === "number" && item.intent_score > 0 && (
            <span>· intent {(item.intent_score * 100).toFixed(0)}%</span>
          )}
          {item.post_url && (
            <a
              href={item.post_url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-0.5 text-[color:var(--sg-primary)] hover:underline"
            >
              open <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export const Customer360Sheet: React.FC<Props> = ({
  open,
  onOpenChange,
  handle,
  onFilterHere,
}) => {
  const navigate = useNavigate();
  const canonical = (handle || "").replace(/^@/, "").trim();
  const [copied, setCopied] = React.useState(false);

  const q = useQuery<Customer360Data>({
    queryKey: ["customer-360", canonical],
    queryFn: async () =>
      (await api.get(`/customers/${encodeURIComponent(canonical)}/360`)).data,
    enabled: open && !!canonical,
    staleTime: 30_000,
  });

  const copyHandle = async () => {
    try {
      await navigator.clipboard.writeText(`@${canonical}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Handle copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const gotoComments = () => {
    navigate(`/comments?author_handle=${encodeURIComponent(canonical)}`);
    onOpenChange(false);
  };
  const gotoLeads = () => {
    navigate(`/leads?author_handle=${encodeURIComponent(canonical)}`);
    onOpenChange(false);
  };
  const gotoComplaints = () => {
    navigate(`/complaints?author_handle=${encodeURIComponent(canonical)}`);
    onOpenChange(false);
  };

  const d = q.data;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="customer-360-sheet"
        className="w-full sm:max-w-xl bg-white overflow-y-auto p-0"
      >
        {/* Gradient header */}
        <div className="relative overflow-hidden">
          <div
            className="absolute inset-0 bg-gradient-to-br from-violet-600 via-indigo-600 to-fuchsia-600 opacity-95"
            aria-hidden
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.25) 0, transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.15) 0, transparent 40%)",
            }}
            aria-hidden
          />
          <div className="relative p-6 pb-8 text-white">
            <SheetHeader>
              <SheetTitle className="text-white font-display tracking-tight text-lg">
                Customer 360
              </SheetTitle>
              <SheetDescription className="sr-only">
                Unified profile of a single social-media handle across every connected platform.
              </SheetDescription>
            </SheetHeader>
            <div className="mt-4 flex items-start gap-4">
              <div className="h-14 w-14 rounded-full bg-white/20 backdrop-blur ring-2 ring-white/30 grid place-items-center text-2xl font-bold uppercase">
                {canonical.slice(0, 2)}
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className="text-xl font-display font-bold truncate"
                  data-testid="c360-display-handle"
                >
                  {d?.display_handle || `@${canonical}`}
                </div>
                {d?.display_name && (
                  <div className="text-sm text-white/80 truncate">
                    {d.display_name}
                  </div>
                )}
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {(d?.platforms || []).map((p) => (
                    <span
                      key={p}
                      className="text-[10px] uppercase tracking-wider bg-white/15 backdrop-blur px-2 py-0.5 rounded-full font-semibold"
                      data-testid={`c360-platform-${p}`}
                    >
                      {p}
                    </span>
                  ))}
                  {(d?.platforms || []).length === 0 && !q.isLoading && (
                    <span className="text-[10px] text-white/70">no platform activity</span>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-[11px] text-white/85">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3 w-3" />
                First seen{" "}
                <b className="text-white" data-testid="c360-first-seen">
                  {d?.first_seen ? timeAgo(d.first_seen) : "—"}
                </b>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                Last seen{" "}
                <b className="text-white" data-testid="c360-last-seen">
                  {d?.last_seen ? timeAgo(d.last_seen) : "—"}
                </b>
              </div>
            </div>
            {/* Quick actions */}
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                data-testid="c360-copy-handle"
                onClick={copyHandle}
                size="sm"
                className="h-8 bg-white/15 hover:bg-white/25 text-white border border-white/20"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 mr-1.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5 mr-1.5" />
                )}
                Copy handle
              </Button>
              {onFilterHere && (
                <Button
                  data-testid="c360-filter-here"
                  onClick={() => {
                    onFilterHere();
                    onOpenChange(false);
                  }}
                  size="sm"
                  className="h-8 bg-white text-violet-700 hover:bg-white/90"
                >
                  <Filter className="h-3.5 w-3.5 mr-1.5" />
                  Filter this list by this author
                </Button>
              )}
            </div>
          </div>
        </div>

        {q.isLoading && (
          <div className="p-6 space-y-4">
            <div className="sg-skeleton h-24" />
            <div className="sg-skeleton h-24" />
            <div className="sg-skeleton h-64" />
          </div>
        )}

        {q.isError && (
          <div className="p-6">
            <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
              Failed to load customer profile. Please try again.
            </div>
          </div>
        )}

        {d && !q.isLoading && (
          <div className="p-6 space-y-6">
            {/* KPI grid */}
            <div className="grid grid-cols-2 gap-2.5">
              <KPI
                label="Comments"
                testId="c360-kpi-comments"
                icon={<MessageSquare className="h-3 w-3" />}
                tone="slate"
                value={d.counts.comments}
                hint={
                  <button
                    type="button"
                    onClick={gotoComments}
                    className="text-[color:var(--sg-primary)] hover:underline"
                    data-testid="c360-goto-comments"
                  >
                    View all →
                  </button>
                }
              />
              <KPI
                label="Leads"
                testId="c360-kpi-leads"
                icon={<Sparkles className="h-3 w-3" />}
                tone="violet"
                value={d.counts.leads}
                hint={
                  d.counts.leads > 0 ? (
                    <button
                      type="button"
                      onClick={gotoLeads}
                      className="text-[color:var(--sg-primary)] hover:underline"
                      data-testid="c360-goto-leads"
                    >
                      Open in Leads →
                    </button>
                  ) : (
                    <span>no active leads</span>
                  )
                }
              />
              <KPI
                label="Complaints"
                testId="c360-kpi-complaints"
                icon={<ShieldAlert className="h-3 w-3" />}
                tone={d.counts.complaints > 0 ? "rose" : "slate"}
                value={d.counts.complaints}
                hint={
                  d.counts.complaints > 0 ? (
                    <button
                      type="button"
                      onClick={gotoComplaints}
                      className="text-[color:var(--sg-primary)] hover:underline"
                      data-testid="c360-goto-complaints"
                    >
                      Open in Complaints →
                    </button>
                  ) : (
                    <span>no complaints</span>
                  )
                }
              />
              <KPI
                label="Avg intent score"
                testId="c360-kpi-intent"
                icon={<TrendingUp className="h-3 w-3" />}
                tone={d.intent_score_avg >= 0.6 ? "emerald" : "amber"}
                value={`${(d.intent_score_avg * 100).toFixed(0)}%`}
                hint={
                  <span>
                    peak <b>{(d.intent_score_max * 100).toFixed(0)}%</b>
                  </span>
                }
              />
            </div>

            {/* Intent distribution */}
            {Object.keys(d.intent_breakdown || {}).length > 0 && (
              <section data-testid="c360-intent-section">
                <div className="sg-label mb-2 flex items-center gap-1.5">
                  <Users className="h-3 w-3" /> Intent distribution
                </div>
                <IntentBar breakdown={d.intent_breakdown} />
              </section>
            )}

            {/* Response stats */}
            {d.response_stats.total_sent > 0 && (
              <section data-testid="c360-response-section">
                <div className="sg-label mb-2 flex items-center gap-1.5">
                  <Zap className="h-3 w-3" /> Your response history
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg border border-[color:var(--sg-border)] bg-white p-2.5">
                    <div className="text-lg font-bold text-[color:var(--sg-fg)]">
                      {d.response_stats.total_sent}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)]">
                      Replies sent
                    </div>
                  </div>
                  <div className="rounded-lg border border-[color:var(--sg-border)] bg-white p-2.5">
                    <div
                      className="text-lg font-bold text-[color:var(--sg-fg)]"
                      data-testid="c360-avg-response"
                    >
                      {formatDuration(d.response_stats.avg_response_time_seconds ?? null)}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)]">
                      Avg response time
                    </div>
                  </div>
                  <div className="rounded-lg border border-[color:var(--sg-border)] bg-white p-2.5">
                    <div className="text-lg font-bold text-emerald-700">
                      {formatDuration(d.response_stats.fastest_response_seconds ?? null)}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)]">
                      Fastest reply
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Top keywords */}
            {(d.top_keywords || []).length > 0 && (
              <section data-testid="c360-keywords-section">
                <div className="sg-label mb-2">Top keywords</div>
                <div className="flex flex-wrap gap-1.5">
                  {d.top_keywords.map((k) => (
                    <span
                      key={k.keyword}
                      data-testid={`c360-keyword-${k.keyword}`}
                      className="inline-flex items-center gap-1 text-[11px] bg-slate-100 text-slate-700 border border-slate-200 rounded-full px-2 py-0.5"
                    >
                      {k.keyword}
                      <b className="text-[color:var(--sg-muted)] font-semibold">
                        {k.count}
                      </b>
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Timeline */}
            <section data-testid="c360-timeline-section">
              <div className="sg-label mb-2 flex items-center justify-between">
                <span>Activity timeline</span>
                {d.timeline.length > 0 && (
                  <span className="text-[10px] normal-case tracking-normal text-[color:var(--sg-muted)]">
                    Showing {d.timeline.length} most recent
                  </span>
                )}
              </div>
              {d.timeline.length === 0 ? (
                <div className="rounded-md border border-dashed border-[color:var(--sg-border)] p-6 text-center text-sm text-[color:var(--sg-muted)]">
                  No activity from this customer yet.
                </div>
              ) : (
                <div
                  className="relative border-l-2 border-slate-100 pl-2"
                  data-testid="c360-timeline"
                >
                  {d.timeline.map((item) => (
                    <TimelineEntry key={`${item.type}-${item.id}`} item={item} />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default Customer360Sheet;
